import { Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createSelector, MemoizedSelector, Selector } from '@ngrx/store';

import * as fromReducer from './category-airplay-security-profile-preset-configuration.reducer';
import * as fromState from './category-airplay-security-profile-preset-configuration.state';

import { CategoryAirPlaySecurityProfileConfiguration } from '@era/settings-sdk';
import { AppleAirPlaySecurityPreset } from '@rocksoft/mdm-sdk-v2';
import { selectAllAppleProfilePresets } from '../apple-profile-preset';
import { Category } from '../router/category';

// ============================================================================
// STATE SELECTORS
// ============================================================================

const selectCategoryAirplaySecurityProfilePresetConfigurationState =
  createFeatureSelector<fromState.CategoryAirplaySecurityProfilePresetConfigurationState>(
    'categoryAirplaySecurityProfilePresetConfiguration'
  );

export const selectAllCategoryAirplaySecurityProfilePresetConfigurations: Selector<
  object,
  CategoryAirPlaySecurityProfileConfiguration[]
> = createSelector(
  selectCategoryAirplaySecurityProfilePresetConfigurationState,
  fromReducer.selectAllCategoryAirplaySecurityProfilePresetConfigurations
);

export const selectCategoryAirplaySecurityProfilePresetConfigurationEntities = createSelector(
  selectCategoryAirplaySecurityProfilePresetConfigurationState,
  fromReducer.selectCategoryAirplaySecurityProfilePresetConfigurationEntities
);

// ============================================================================
// GENERIC SELECTORS
// ============================================================================

/**
 * Generic selector for airplay security profile preset configuration linked to a category.
 * Automatically handles router selector mapping.
 *
 * Usage in containers:
 * this.linkedConfiguration$ = this.store.select(selectAirplaySecurityProfilePresetConfigurationLinkedToCategory(category));
 */
export const selectAirplaySecurityProfilePresetConfigurationLinkedToCategory = (category: Category) => {
  return createSelector(
    selectCategoryAirplaySecurityProfilePresetConfigurationEntities,
    (configurations: Dictionary<CategoryAirPlaySecurityProfileConfiguration>) => {
      if (!category || !configurations) {
        return undefined;
      }
      // Entity keys are `${type}-${id}-${presetId}`, so we need to find by matching type and id
      const configKey = Object.keys(configurations).find(
        (key) =>
          configurations[key]?.categoryId === category.categoryId &&
          configurations[key]?.categoryType === category.categoryType
      );
      return configKey ? configurations[configKey] : undefined;
    }
  );
};

/**
 * Generic selector for airplay security profile preset linked to a category type.
 * Automatically handles router selector mapping.
 *
 * Usage in containers:
 * this.linkedPreset$ = this.store.select(selectAirplaySecurityPresetLinkedToCategory(category));
 */
export const selectAirplaySecurityPresetLinkedToCategory = (
  category: Category
): MemoizedSelector<object, AppleAirPlaySecurityPreset | undefined> => {
  return createSelector(
    selectAirplaySecurityProfilePresetConfigurationLinkedToCategory(category),
    selectAllAppleProfilePresets,
    (configuration, allPresets) => {
      if (!configuration) {
        return undefined;
      }
      // Find preset by uniqueId (stored in configuration.airPlaySecurityProfilePresetId)
      const presetEntry = allPresets.find(
        (entry) =>
          entry.profileType === 'airplay-security' &&
          entry.preset.uniqueId === configuration.airPlaySecurityProfilePresetId
      );
      return presetEntry?.preset;
    }
  );
};

/**
 * Generic selector for checking if airplay security profile preset is linked to any category type.
 *
 * Usage in containers:
 * this.hasProfile$ = this.store.select(selectHasAirplaySecurityProfilePresetLinkedToCategory(category));
 */
export const selectHasAirplaySecurityProfilePresetLinkedToCategory = (
  category: Category
): MemoizedSelector<object, boolean> => {
  return createSelector(selectAirplaySecurityPresetLinkedToCategory(category), (preset) => !!preset);
};

/**
 * Generic selector for airplay security profile preset applied to a category type (with inheritance).
 * Location inherits from Brand, Room inherits from Location (or Brand if Location has none).
 *
 * Usage in containers:
 * this.appliedPreset$ = this.store.select(selectAirplaySecurityPresetAppliedToCategory(category));
 */
export const selectAirplaySecurityPresetAppliedToCategory = (
  category: Category
): MemoizedSelector<object, AppleAirPlaySecurityPreset | undefined> => {
  if (category.parent) {
    return createSelector(
      selectAirplaySecurityPresetAppliedToCategory(category.parent),
      selectAirplaySecurityPresetLinkedToCategory(category),
      (parent, current) => current ?? parent
    );
  } else {
    return selectAirplaySecurityPresetLinkedToCategory(category);
  }
};
